import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, type SupportedLanguage } from "@/i18n";

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  supported: readonly SupportedLanguage[];
  labels: Record<SupportedLanguage, string>;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();

  // Keep <html lang> in sync with the active UI locale on every change
  useEffect(() => {
    const apply = (lng: string) => {
      if (typeof document !== "undefined") {
        document.documentElement.lang = (lng || "en").slice(0, 2);
      }
    };
    apply(i18n.language);
    i18n.on("languageChanged", apply);
    return () => {
      i18n.off("languageChanged", apply);
    };
  }, [i18n]);

  // hydrate from profile
  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      const userId = data.user?.id;
      if (!userId) return;
      supabase
        .from("profiles")
        .select("language")
        .eq("id", userId)
        .maybeSingle()
        .then(({ data: profile }) => {
          if (!mounted || !profile?.language) return;
          if (!localStorage.getItem("bp.language")) {
            void i18n.changeLanguage(profile.language);
          }
        });
    });
    return () => {
      mounted = false;
    };
  }, [i18n]);

  const language = (SUPPORTED_LANGUAGES.includes(i18n.language as SupportedLanguage)
    ? i18n.language
    : "en") as SupportedLanguage;

  const setLanguage = (lang: SupportedLanguage) => {
    void i18n.changeLanguage(lang);
    localStorage.setItem("bp.language", lang);
    supabase.auth.getUser().then(({ data }) => {
      const userId = data.user?.id;
      if (!userId) return;
      void supabase.from("profiles").update({ language: lang }).eq("id", userId);
    });
  };

  const value = useMemo(
    () => ({ language, setLanguage, supported: SUPPORTED_LANGUAGES, labels: LANGUAGE_LABELS }),
    [language],
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (ctx) return ctx;
  // Safe fallback: tolerate consumers that mount before/outside the provider
  // (e.g. error-boundary fallbacks, HMR transitional renders). Falls back to
  // i18next directly so the UI keeps working instead of crashing the tree.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { i18n } = useTranslation();
  const language = (SUPPORTED_LANGUAGES.includes(i18n.language as SupportedLanguage)
    ? i18n.language
    : "en") as SupportedLanguage;
  return {
    language,
    setLanguage: (lang: SupportedLanguage) => {
      void i18n.changeLanguage(lang);
      try { localStorage.setItem("bp.language", lang); } catch { /* noop */ }
    },
    supported: SUPPORTED_LANGUAGES,
    labels: LANGUAGE_LABELS,
  };
}
